import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Sparkles, Plus, X, Save, Loader2, UserCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const TagInput = ({ value, onChange, placeholder, theme, dataTestid }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(''); return; }
    onChange([...value, v]);
    setDraft('');
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid={dataTestid}>
      <div className="flex flex-wrap gap-1.5">
        {value.map((t, i) => (
          <span key={i} className="text-[11px] px-2 py-1 rounded-full inline-flex items-center gap-1.5"
            style={{ background: `${theme.accent}20`, color: theme.accent, border: `1px solid ${theme.accent}40` }}>
            {t}
            <button onClick={() => remove(i)} aria-label="Quitar"
              className="opacity-70 hover:opacity-100" style={{ color: theme.accent }}>
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 text-sm px-3 py-2 rounded-md outline-none"
          style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}
        />
        <button onClick={add} type="button"
          className="px-3 rounded-md text-sm" style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}>
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
};

const Profile = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bio, setBio] = useState('');
  const [interests, setInterests] = useState([]);
  const [goalsFocus, setGoalsFocus] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get('/profile')
      .then((r) => {
        if (cancelled) return;
        setBio(r.data?.bio || '');
        setInterests(r.data?.interests || []);
        setGoalsFocus(r.data?.goals_focus || []);
      })
      .catch(() => toast.error('No pude cargar tu perfil.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/profile', { bio, interests, goals_focus: goalsFocus });
      toast.success('Perfil actualizado');
    } catch {
      toast.error('No pude guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const runOnboarding = () => {
    window.dispatchEvent(new Event('teko-onboarding-open'));
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <UserCircle size={22} style={{ color: theme.accent }} />
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ color: theme.textPrimary }}>Mi Perfil</h1>
        </div>
        <p className="text-sm" style={{ color: theme.textSecondary }}>
          Información que ayuda a la IA de Teko a entenderte mejor. Solo tú la ves.
        </p>
      </header>

      {loading ? (
        <div className="text-center py-12" style={{ color: theme.textMuted }}>
          <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Cargando...
        </div>
      ) : (
        <div className="space-y-6">
          {/* Identidad básica (read-only) */}
          <section className="rounded-2xl p-5 space-y-1"
            style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Cuenta</p>
            <p className="text-base font-medium" style={{ color: theme.textPrimary }}>{user?.name || 'Sin nombre'}</p>
            <p className="text-xs" style={{ color: theme.textMuted }}>{user?.email}</p>
          </section>

          {/* Resumen / Bio */}
          <section className="rounded-2xl p-5 space-y-3"
            style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Resumen personal</p>
                <p className="text-xs" style={{ color: theme.textSecondary }}>Una descripción breve sobre ti (estilo, contexto, momento de vida).</p>
              </div>
              <button onClick={runOnboarding}
                className="text-[11px] px-2.5 py-1.5 rounded-md inline-flex items-center gap-1.5"
                style={{ background: `${theme.accent}20`, color: theme.accent, border: `1px solid ${theme.accent}40` }}
                data-testid="profile-rerun-onboarding">
                <Sparkles size={11} /> Volver a generarlo con IA
              </button>
            </div>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={5}
              maxLength={1200}
              placeholder="Aún no has descrito tu perfil. Puedes escribirlo a mano o pulsar 'Volver a generarlo con IA'."
              className="w-full text-sm p-3 rounded-md outline-none"
              style={{ background: theme.elevated, color: theme.textPrimary, border: `1px solid ${theme.border}` }}
              data-testid="profile-bio"
            />
            <p className="text-[10px] text-right" style={{ color: theme.textMuted }}>{bio.length} / 1200</p>
          </section>

          {/* Intereses */}
          <section className="rounded-2xl p-5 space-y-3"
            style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <div>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Intereses</p>
              <p className="text-xs" style={{ color: theme.textSecondary }}>Áreas que te importan (salud, escritura, idiomas, finanzas...).</p>
            </div>
            <TagInput value={interests} onChange={setInterests}
              placeholder="Escribe un interés y pulsa Enter..." theme={theme}
              dataTestid="profile-interests" />
          </section>

          {/* Goals focus */}
          <section className="rounded-2xl p-5 space-y-3"
            style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <div>
              <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Foco actual</p>
              <p className="text-xs" style={{ color: theme.textSecondary }}>Objetivos o áreas en las que quieres trabajar los próximos meses.</p>
            </div>
            <TagInput value={goalsFocus} onChange={setGoalsFocus}
              placeholder="Escribe un objetivo y pulsa Enter..." theme={theme}
              dataTestid="profile-goals-focus" />
          </section>

          <div className="flex justify-end gap-2 sticky bottom-4">
            <Button onClick={save} disabled={saving}
              className="inline-flex items-center gap-2"
              style={{ background: theme.accent, color: theme.bg }}
              data-testid="profile-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
